// Shared helpers for the notice components: the calls to the API, and the
// wording of periods and dates. Everything which talks to the data provider is
// in this file, so if your provider builds URLs differently from what is
// assumed below, this is the only place to change.

/**
 * GET a single object from an endpoint, e.g. getObject(dataProvider,
 * 'employees/12/notice') for GET employees/12/notice.
 *
 * The whole path is passed as the resource, never as ('employees', id):
 * react-admin caches every getOne result against its resource by `id`, and
 * these responses carry the employment's or production's id, so fetching them
 * as 'employees' would overwrite the cached employment record with the notice
 * status. A path react-admin does not know about is not cached.
 *
 * basicRestProvider.getOne requests `${apiUrl}/${resource}/${id}`, so the empty
 * id leaves a trailing slash, which Laravel ignores. A query string can be
 * passed and is carried in the id, after the slash.
 */
export const getObject = (dataProvider, path, query = null) => {
    const qs = query ? new URLSearchParams(query).toString() : '';

    return dataProvider.getOne(path, { id: qs ? `?${qs}` : '' }).then(({ data }) => data);
};

/**
 * POST to an endpoint, the same way the deal, contract template and penalty
 * rate panels do: dataProvider.create with the full path.
 *
 * basicRestProvider.create discards the server's response (it returns the
 * request data plus the id), so this only confirms the POST succeeded. To see
 * the result, fetch it again with getObject.
 */
export const post = (dataProvider, path, data = {}) =>
    dataProvider.create(path, { data }).then(() => undefined);

export const errorMessage = (err, fallback) =>
    err?.body?.message ?? err?.message ?? fallback;

/**
 * 7 -> "1 week", 14 -> "2 weeks", 10 -> "10 days". The same wording as the
 * emails.
 */
export const describePeriod = (days) => {
    const n = Number(days);

    if (!Number.isInteger(n) || n < 1) {
        return '';
    }

    if (n % 7 === 0) {
        const weeks = n / 7;
        return weeks === 1 ? '1 week' : `${weeks} weeks`;
    }

    return n === 1 ? '1 day' : `${n} days`;
};

/**
 * "Y-m-d" or an ISO timestamp -> "9 October 2026". A bare date is read as a
 * calendar date, not shifted by the browser's timezone.
 */
export const formatDate = (value) => {
    if (!value) {
        return '';
    }

    const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    const date = bare ? new Date(Number(bare[1]), Number(bare[2]) - 1, Number(bare[3])) : new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * "9 October 2026, 14:05" for when something happened.
 */
export const formatDateTime = (value) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return `${formatDate(value)}, ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Days until (positive) or since (negative) a due date -> words.
 */
export const describeDueIn = (days) => {
    if (days === 0) {
        return 'due today';
    }

    if (days > 0) {
        return days === 1 ? 'due tomorrow' : `due in ${days} days`;
    }

    const ago = Math.abs(days);

    return ago === 1 ? 'was due yesterday' : `was due ${ago} days ago`;
};